import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from 'react-router-dom';

import 'rsuite/dist/rsuite.min.css';
import './styles/index.scss';

import Login from './pages/login/Login';
import Navigator from './navigator/Navigator';
import { AuthProvider, useAuth } from './common/AuthContext';
import { installResizeObserverErrorSuppression } from './common/resizeObserverErrorSuppression';

installResizeObserverErrorSuppression();

/** Full-page placeholder shown while the session check is in flight. */
function PageLoader() {
  return <div className="page-loader" aria-busy="true" />;
}

/**
 * Route guard. Shows a loader while the session is being checked, then
 * renders `children` once the session matches `allow`, or redirects.
 *
 * @param props
 */
function SessionGate({
  allow,
  redirectTo,
  children,
}: {
  allow: 'authed' | 'guest';
  redirectTo: string;
  children: React.ReactNode;
}) {
  const { status } = useAuth();

  if (status === 'checking') {
    return <PageLoader />;
  }
  if (status !== allow) {
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
}

function App() {
  React.useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
      return;
    }
    // Legacy CRA cleanup: drop any previously-registered service worker and
    // its caches so stale assets are never served to returning users.
    navigator.serviceWorker.ready.then(registration => {
      registration.unregister();
      if (typeof caches !== 'undefined') {
        caches.keys().then(async names => {
          await Promise.all(names.map(name => caches.delete(name)));
        });
      }
    });
  }, []);

  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/login"
            element={
              <SessionGate allow="guest" redirectTo="/dashboard">
                <Login />
              </SessionGate>
            }
          />
          <Route
            path="/*"
            element={
              <SessionGate allow="authed" redirectTo="/login">
                <Navigator />
              </SessionGate>
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

const rootElement = document.getElementById('root');
if (rootElement) {
  createRoot(rootElement).render(<App />);
}
